// Live preview of what a channel's brand colors + logo actually look like.
//
// Shows step 1 of the advertiser wizard, including the header, because that's
// where a publisher's colors have lasting effect. The landing page hero is
// deliberately NOT previewed: once the stitched background video is attached it
// covers the brand gradient, so a preview of it would misrepresent the result.
//
// This is a static mock, not the real components: SelfServiceWizard needs auth
// and wizard state, and PublicHeader needs a full PublisherConfig. What keeps
// it honest is that every brand-driven value is copied from the real recipes
// and uses the same tintFromAccent() those surfaces use.
//
// Recipes mirrored:
//   accent    = accentColor || primaryColor      (SelfServiceWizard.tsx:1756)
//   primary   = primaryColor || accent
//   header    = light bg + accent hairline       (PublicHeader.tsx:71,161)
//   step chip = color accent on tint(accent,.05) (steps/_shared.tsx StepHeader)
//   Continue  = linear-gradient(180deg, accent, primary), white text

import React from 'react';
import { tintFromAccent } from '../../steps/_shared';
import { labelClass } from './publisherFieldStyles';
import type { BrandingValues } from './types';

const HEX_RE = /^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/;

interface Props {
  values: BrandingValues;
  /** Shown in the mock header's fallback chip. */
  channelName?: string;
  /**
   * Explain where the secondary colour does show up. Suppressed on surfaces
   * that don't offer the field at all, where the note would only raise a
   * question the form can't answer.
   */
  showSecondaryNote?: boolean;
}

const BrandingPreview: React.FC<Props> = ({
  values,
  channelName,
  showSecondaryNote = true,
}) => {
  const primaryValid = HEX_RE.test(values.primaryColor.trim());
  const accentValid = HEX_RE.test(values.accentColor.trim());

  // Same fallback chain the real wizard uses.
  const accent = (accentValid ? values.accentColor : values.primaryColor).trim();
  const primary = (primaryValid ? values.primaryColor : values.accentColor).trim();

  if (!primaryValid && !accentValid) {
    return (
      <div className="space-y-2">
        <span className={labelClass}>Preview</span>
        <div className="rounded-xl border border-dashed border-slate-300 bg-slate-100 px-4 py-6 text-center">
          <p className="text-[12px] text-slate-500">
            Pick a brand color to see how your channel will look. This preview updates
            as you go.
          </p>
        </div>
      </div>
    );
  }

  const name = channelName?.trim() || 'your channel';
  const initial = name.charAt(0).toUpperCase();

  return (
    <div className="space-y-2">
      <div className="flex items-baseline gap-2 flex-wrap">
        <span className={`${labelClass} mb-0`}>Preview</span>
        <span className="text-[11px] text-slate-500">
          Not part of your form. Updates live as you change your colors and logo.
        </span>
      </div>

      {/* Sat on a tinted, dashed surface so it reads as a picture of another
          screen rather than more fields to fill in. */}
      <div className="rounded-xl border border-dashed border-slate-300 bg-slate-100 p-3">
      <figure className="m-0 rounded-xl border border-slate-200 overflow-hidden bg-white shadow-sm">
        {/* Header. Light background in the real app, which is why forcing the
            logo to white can hide it entirely. */}
        <div className="flex items-center gap-2 px-4 py-3 bg-white">
          {values.logoUrl ? (
            <img
              src={values.logoUrl}
              alt=""
              className={`h-10 w-auto max-w-[180px] object-contain ${values.logoInvert ? 'brightness-0 invert' : ''}`}
              style={values.logoScale !== 1
                ? { transform: `scale(${values.logoScale})`, transformOrigin: 'left center' }
                : undefined}
            />
          ) : (
            <span
              className="h-6 w-6 rounded-md flex items-center justify-center text-white text-[11px] font-semibold"
              style={{ backgroundColor: accent }}
            >
              {initial}
            </span>
          )}
          <span className="ml-auto text-[9px] text-slate-400">Powered by CTVBuyer</span>
        </div>
        <div
          className="h-px"
          style={{ background: `linear-gradient(90deg, transparent, ${tintFromAccent(accent, 0.55)}, transparent)` }}
        />

        {/* Step 1 body. */}
        <div className="px-4 py-4 space-y-3">
          <div className="flex gap-1">
            <span
              className="h-1 flex-1 rounded-full"
              style={{ background: `linear-gradient(90deg, ${accent}, ${tintFromAccent(accent, 0.35)})` }}
            />
            {[0, 1, 2, 3].map((i) => (
              <span key={i} className="h-1 flex-1 rounded-full bg-slate-200" />
            ))}
          </div>

          <span
            className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wide"
            style={{ backgroundColor: tintFromAccent(accent, 0.05), color: accent }}
          >
            <span className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: accent }} />
            Step 1 · Your business
          </span>

          <div
            className="rounded-lg border border-slate-100 p-3 space-y-2"
            style={{
              boxShadow: `0 1px 0 rgba(15,23,42,0.02), 0 10px 24px -16px ${tintFromAccent(accent, 0.4)}`,
            }}
          >
            {['Business name', 'Contact email'].map((field) => (
              <div key={field} className="rounded-md border border-slate-200 px-2.5 py-1.5">
                <span className="block text-[9px] text-slate-400">{field}</span>
                <span className="block mt-0.5 h-2 w-2/3 rounded bg-slate-100" />
              </div>
            ))}
          </div>

          <div className="flex items-center gap-2">
            <span
              className="h-3 w-3 rounded-[4px] shrink-0"
              style={{ backgroundColor: accent, borderColor: accent }}
            />
            <span className="text-[10px] text-slate-500">
              I agree to the <span style={{ color: accent }}>Advertiser Terms</span>
            </span>
          </div>

          <div className="flex justify-end">
            <span
              className="rounded-full px-4 py-1.5 text-[11px] font-semibold text-white"
              style={{
                background: `linear-gradient(180deg, ${accent}, ${primary})`,
                boxShadow: `0 6px 16px -8px ${primary}`,
              }}
            >
              Continue
            </span>
          </div>
        </div>

        <figcaption className="px-4 py-1.5 text-[10px] text-slate-500 border-t border-slate-100">
          How step 1 of the advertiser&rsquo;s wizard will look
        </figcaption>
      </figure>
      </div>

      {/* Campaign reports are themed to the ADVERTISER's scraped brand, not
          the station's (CampaignReportPage.tsx:177), so the creative-slot
          palette is the publisher's secondary colour's only real consumer. */}
      {(showSecondaryNote || (values.logoInvert && values.logoUrl)) && (
        <p className="text-[11px] text-slate-500">
          {showSecondaryNote && (
            <>
              Your secondary color doesn&rsquo;t appear on this screen. It pairs with your
              primary to color the creative slots when an advertiser schedules multiple ads.
            </>
          )}
          {values.logoInvert && values.logoUrl && (
            <>
              {showSecondaryNote ? ' ' : ''}
              Forcing the logo to white will hide it, because this header is light.
            </>
          )}
        </p>
      )}
    </div>
  );
};

export default BrandingPreview;
